import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { APPRECIATION_NOTE, getMentionBac, CYCLES } from '../utils/cycle.helpers';

export default function BulletinSecondaire() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const periodId = searchParams.get('periodId');
  const cycleParam = searchParams.get('cycle');
  const navigate = useNavigate();

  const [student, setStudent]     = useState(null);
  const [subjects, setSubjects]   = useState([]);
  const [school, setSchool]       = useState(null);
  const [period, setPeriod]       = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [stats, setStats]         = useState({ moyenneGenerale: null, rang: null, moyenneClasse: null, effectif: null, tauxReussite: null });
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const isSecondaire = cycleParam === CYCLES.SECONDAIRE || cycleParam === 'SECONDAIRE';

  useEffect(() => {
    async function load() {
      try {
        const [stu, schoolData, periods] = await Promise.all([
          api.get(`/students/${studentId}`),
          api.get('/establishment').catch(() => null),
          api.get('/catalog/periods').catch(() => []),
        ]);
        setStudent(stu);
        setSchool(schoolData);
        const per = (periods || []).find((p) => p.id === periodId) || periods?.[0];
        setPeriod(per);

        const enrollment = (stu.enrollments || [])[0];
        const clsId = enrollment?.classId;
        setClassInfo(enrollment?.class || { name: '—' });
        if (!clsId) return;

        // Charger les évaluations de la classe
        const evals = await api.get(`/evaluations?classId=${clsId}${periodId ? `&periodId=${periodId}` : ''}`).catch(() => []);

        // Regrouper par matière
        const subjectMap = {};
        (evals || []).forEach((ev) => {
          const sid = ev.subjectId;
          if (!subjectMap[sid]) {
            subjectMap[sid] = {
              subjectId: sid,
              subjectName: ev.subject?.name || '—',
              coefficient: ev.coefficient || ev.subject?.defaultCoefficient || 1,
              notes: [],
              devoir: null,
              composition: null,
              examenBlanc: null,
            };
          }
          const result = (ev.results || []).find((r) => r.studentId === studentId);
          const note = result?.totalObtained ?? null;
          const absent = result?.absent ?? false;
          const sur20 = (note != null && !absent) ? Math.round(note / ev.baremeTotal * 20 * 100) / 100 : null;

          subjectMap[sid].notes.push({ type: ev.type, note, sur20, bareme: ev.baremeTotal, absent });

          if (['CONTROLE', 'DEVOIR'].includes(ev.type))   subjectMap[sid].devoir      = { note, sur20, bareme: ev.baremeTotal, absent };
          if (ev.type === 'COMPOSITION')                   subjectMap[sid].composition  = { note, sur20, bareme: ev.baremeTotal, absent };
          if (ev.type === 'EXAMEN_BLANC')                  subjectMap[sid].examenBlanc  = { note, sur20, bareme: ev.baremeTotal, absent };
        });

        // Calculer moyenne par matière (devoir coeff 1, compo coeff 2)
        const subjectList = Object.values(subjectMap).map((s) => {
          const parts = [];
          if (s.devoir?.sur20 != null)      parts.push({ val: s.devoir.sur20,      w: 1 });
          if (s.composition?.sur20 != null) parts.push({ val: s.composition.sur20, w: 2 });
          if (parts.length === 0 && s.notes.some((n) => n.sur20 != null)) {
            s.notes.filter((n) => n.sur20 != null).forEach((n) => parts.push({ val: n.sur20, w: 1 }));
          }
          const totalW = parts.reduce((sum, p) => sum + p.w, 0);
          const moyenne = totalW > 0 ? Math.round(parts.reduce((sum, p) => sum + p.val * p.w, 0) / totalW * 100) / 100 : null;
          return { ...s, moyenne };
        });

        setSubjects(subjectList);

        // Moyenne générale pondérée par coefficient matière
        const noted = subjectList.filter((s) => s.moyenne != null);
        const totalCoeff = noted.reduce((sum, s) => sum + s.coefficient, 0);
        const moyGen = totalCoeff > 0
          ? Math.round(noted.reduce((sum, s) => sum + s.moyenne * s.coefficient, 0) / totalCoeff * 100) / 100
          : null;

        // Rang dans la classe
        if (clsId && moyGen != null) {
          const allStudents = await api.get(`/students?classId=${clsId}`).catch(() => []);
          const moyennes = [];
          for (const other of (allStudents || [])) {
            const otherNotes = [];
            (evals || []).forEach((ev) => {
              const r = (ev.results || []).find((res) => res.studentId === other.id);
              if (r?.totalObtained != null && !r.absent) {
                otherNotes.push({ sur20: r.totalObtained / ev.baremeTotal * 20, coeff: ev.coefficient || 1 });
              }
            });
            const tc = otherNotes.reduce((s, n) => s + n.coeff, 0);
            const moy = tc > 0 ? otherNotes.reduce((s, n) => s + n.sur20 * n.coeff, 0) / tc : null;
            if (moy != null) moyennes.push(moy);
          }
          moyennes.sort((a, b) => b - a);
          const rang = moyennes.findIndex((m) => m <= moyGen) + 1 || null;
          const moyClasse = moyennes.length > 0 ? Math.round(moyennes.reduce((s, v) => s + v, 0) / moyennes.length * 100) / 100 : null;
          const taux = moyennes.length > 0 ? Math.round(moyennes.filter((m) => m >= 10).length / moyennes.length * 100) : null;
          setStats({ moyenneGenerale: moyGen, rang, moyenneClasse: moyClasse, effectif: moyennes.length, tauxReussite: taux });
        } else {
          setStats({ moyenneGenerale: moyGen, rang: null, moyenneClasse: null, effectif: null, tauxReussite: null });
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [studentId, periodId]);

  if (loading) return <div className="loading">Génération du bulletin…</div>;
  if (error)   return <div className="form-error">{error}</div>;
  if (!student) return <div className="empty">Élève introuvable.</div>;

  const { moyenneGenerale, rang, moyenneClasse, effectif, tauxReussite } = stats;
  const appreciation  = moyenneGenerale != null ? APPRECIATION_NOTE(moyenneGenerale, 20) : '—';
  const mention       = isSecondaire && moyenneGenerale != null ? getMentionBac(moyenneGenerale) : null;
  const annee         = new Date().getFullYear();

  // Groupes de matières selon le cycle
  const GROUPES_COLLEGE = {
    'Littéraire':         ['Français', 'Histoire', 'Géographie', 'Éducation civique'],
    'Scientifique':       ['Mathématiques', 'Sciences de la Vie', 'Physique'],
    'Langues':            ['Anglais', 'Allemand', 'Espagnol', 'Arabe'],
    'Arts & Sport':       ['EPS', 'Arts', 'Musique', 'Dessin'],
    'Autres matières':    [],
  };
  const GROUPES_LYCEE = {
    'Philosophie & Lettres': ['Philosophie', 'Français'],
    'Sciences':              ['Mathématiques', 'Sciences Physiques', 'Sciences de la Vie', 'Informatique'],
    'Sciences humaines':     ['Histoire', 'Géographie', 'Éducation civique'],
    'Langues':               ['Anglais', 'Allemand', 'Espagnol'],
    'Arts & Sport':          ['EPS', 'Arts'],
    'Autres matières':       [],
  };
  const GROUPES = isSecondaire ? GROUPES_LYCEE : GROUPES_COLLEGE;

  function getGroupe(name = '') {
    for (const [g, mats] of Object.entries(GROUPES)) {
      if (mats.some((m) => name.includes(m))) return g;
    }
    return 'Autres matières';
  }

  const byGroupe = {};
  subjects.forEach((s) => {
    const g = getGroupe(s.subjectName);
    if (!byGroupe[g]) byGroupe[g] = [];
    byGroupe[g].push(s);
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn-ghost btn-sm" onClick={() => navigate(-1)}>← Retour</button>
        <div style={{ display: 'flex', gap: 8 }}>
          {mention && (
            <span style={{ padding: '4px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: mention.color + '20', color: mention.color }}>
              {mention.label}
            </span>
          )}
          <button className="btn-ghost btn-sm" onClick={() => window.print()}>🖨 Imprimer</button>
        </div>
      </div>

      <div className="bulletin">
        {/* En-tête */}
        <div className="bulletin-head">
          <div className="bulletin-mark">S</div>
          <div className="bulletin-school">
            <strong>{school?.name || 'Groupe Scolaire La Providence'}</strong>
            <span>{school?.address || 'Éducation nationale'}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="bulletin-title">{isSecondaire ? 'Bulletin Lycée' : 'Bulletin Collège'}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Année {annee}-{annee + 1}</div>
          </div>
        </div>

        {/* Identité */}
        <div className="bulletin-identity">
          <div><span>Nom</span><b>{student?.lastName}</b></div>
          <div><span>Prénom</span><b>{student?.firstName}</b></div>
          <div><span>Matricule</span><b>{student?.matricule}</b></div>
          <div><span>Classe</span><b>{classInfo?.name || '—'}</b></div>
        </div>

        {/* Tableau des matières */}
        {subjects.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', border: '0.5px dashed var(--line)', borderRadius: 8, margin: '12px 0' }}>
            Aucune évaluation saisie pour ce trimestre.
          </div>
        ) : (
          Object.entries(byGroupe).filter(([, mats]) => mats.length > 0).map(([groupe, mats]) => (
            <div key={groupe}>
              <div style={{ background: '#1a3a5c', color: '#fff', padding: '5px 12px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 8 }}>
                {groupe}
              </div>
              <table className="bulletin-table">
                <thead>
                  <tr>
                    <th style={{ width: '32%' }}>Matière</th>
                    <th className="num">Devoir /20</th>
                    <th className="num">Composition /20</th>
                    <th className="num">Moyenne /20</th>
                    <th className="num">Coeff.</th>
                    <th className="num">Moy. × Coeff.</th>
                    <th>Appréciation</th>
                  </tr>
                </thead>
                <tbody>
                  {mats.map((mat, i) => {
                    const appr  = mat.moyenne != null ? APPRECIATION_NOTE(mat.moyenne, 20) : '—';
                    const color = mat.moyenne != null ? (mat.moyenne >= 10 ? 'var(--success)' : 'var(--danger)') : 'inherit';
                    const pts   = mat.moyenne != null ? Math.round(mat.moyenne * mat.coefficient * 100) / 100 : null;
                    return (
                      <tr key={i}>
                        <td><b>{mat.subjectName}</b></td>
                        <td className="num mono">
                          {mat.devoir?.absent ? <span style={{ color: 'var(--warn)' }}>Abs</span> : mat.devoir?.sur20 != null ? mat.devoir.sur20 : '—'}
                        </td>
                        <td className="num mono">
                          {mat.composition?.absent ? <span style={{ color: 'var(--warn)' }}>Abs</span> : mat.composition?.sur20 != null ? mat.composition.sur20 : '—'}
                        </td>
                        <td className="num mono" style={{ fontWeight: 700, color }}>{mat.moyenne ?? '—'}</td>
                        <td className="num">{mat.coefficient}</td>
                        <td className="num mono">{pts ?? '—'}</td>
                        <td style={{ fontSize: 12 }}>{appr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )}

        {/* Synthèse */}
        <div className="bulletin-summary" style={{ marginTop: 16 }}>
          <div className="bulletin-average" style={{ background: mention ? mention.color : 'var(--navy)' }}>
            <span>{period?.name || 'Trimestre'}</span>
            <strong>{moyenneGenerale ?? '—'}<em>/20</em></strong>
            {mention && <span style={{ marginTop: 6, fontWeight: 600 }}>{mention.label}</span>}
            {!mention && <span style={{ marginTop: 6 }}>{moyenneGenerale != null ? appreciation : ''}</span>}
          </div>
          <div className="bulletin-kpis">
            <div>
              <span>Rang</span>
              <b>{rang != null && effectif != null ? `${rang} / ${effectif}` : '—'}</b>
            </div>
            <div>
              <span>Moy. de classe</span>
              <b>{moyenneClasse != null ? `${moyenneClasse}/20` : '—'}</b>
            </div>
            <div>
              <span>Taux réussite</span>
              <b style={{ color: tauxReussite != null ? (tauxReussite >= 50 ? 'var(--success)' : 'var(--danger)') : 'inherit' }}>
                {tauxReussite != null ? `${tauxReussite}%` : '—'}
              </b>
            </div>
          </div>
        </div>

        {/* Appréciation du conseil de classe */}
        <div style={{ marginTop: 14, padding: '12px 16px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            Appréciation du Conseil de classe
          </div>
          <div style={{ minHeight: 40, fontSize: 13 }}>—</div>
        </div>

        {/* Absences */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14 }}>
          {['Absences justifiées', 'Absences injustifiées', 'Retards'].map((label) => (
            <div key={label} style={{ textAlign: 'center', padding: 10, border: '0.5px solid var(--line)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontWeight: 700 }}>—</div>
            </div>
          ))}
        </div>

        {/* Signatures */}
        <div className="bulletin-foot" style={{ marginTop: 28 }}>
          {['Le Professeur Principal', 'Le Directeur / Proviseur', 'Les Parents / Tuteurs'].map((sig) => (
            <div key={sig} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 36 }}>{sig}</div>
              <div className="sign-line" style={{ margin: '0 auto' }} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
